import React from "react";
import type { DiffWBSNode } from "../../../models";
import DiffActivityTable from "../DiffActivityTable/DiffActivityTable";
import "./DiffTreeNode.css";

interface Props {
  node: DiffWBSNode;
  depth: number;
  expandedPaths: Set<string>;
  onToggle: (wbsPath: string) => void;
  canRevert?: boolean;
  reverting?: string | null;
  onRevertField?: (taskCode: string, field: string) => void;
  onRevertActivity?: (taskCode: string) => void;
}

const DiffTreeNode: React.FC<Props> = ({ node, depth, expandedPaths, onToggle, canRevert, reverting, onRevertField, onRevertActivity }) => {
  const expanded = expandedPaths.has(node.wbs_path);
  const indent = depth * 20 + 12;
  const hasChildren = node.children.length > 0 || node.activities.length > 0;

  const added = node.activities.filter((a) => a.type === "added").length;
  const changed = node.activities.filter((a) => a.type === "changed").length;
  const removed = node.activities.filter((a) => a.type === "removed").length;

  const handleClick = () => {
    if (!hasChildren) return;
    onToggle(node.wbs_path);
  };

  return (
    <div className="diff-tree-node" data-wbs-path={node.wbs_path}>
      <button
        className={`diff-tree-node__header diff-tree-node__header--${node.node_status}`}
        style={{ paddingLeft: `${indent}px` }}
        onClick={handleClick}
      >
        <span
          className={`diff-tree-node__chevron ${
            hasChildren ? "" : "diff-tree-node__chevron--hidden"
          } ${expanded ? "diff-tree-node__chevron--expanded" : ""}`}
        >
          &#9654;
        </span>

        <span className="diff-tree-node__name" title={node.name}>
          {node.name}
        </span>

        {node.node_status !== "unchanged" && (
          <span className={`diff-tree-node__badge diff-tree-node__badge--${node.node_status}`}>
            {node.node_status}
            {node.node_status === "renamed" && node.old_name && (
              <span className="diff-tree-node__old-name">
                {" "}(was: {node.old_name})
              </span>
            )}
          </span>
        )}

        {node.node_status === "moved" && node.move_from && node.move_from.length > 0 && (
          <span className="diff-tree-node__move-annotation" title={node.move_from.map(m => m.from_path).join(", ")}>
            {"← "}
            {node.move_from.map((m, i) => (
              <span key={m.from_path}>
                {i > 0 && ", "}
                {m.from_name} ({m.overlap_pct}%)
              </span>
            ))}
          </span>
        )}

        {node.node_status === "removed" && node.moved_to && node.moved_to.length > 0 && (
          <span className="diff-tree-node__move-annotation" title={node.moved_to.map(m => m.from_path).join(", ")}>
            {"→ "}
            {node.moved_to.map((m, i) => (
              <span key={m.from_path}>
                {i > 0 && ", "}
                {m.from_name}
              </span>
            ))}
          </span>
        )}

        <span className="diff-tree-node__pills">
          {added > 0 && <span className="diff-pill diff-pill--added">+{added}</span>}
          {changed > 0 && <span className="diff-pill diff-pill--changed">{changed}</span>}
          {removed > 0 && <span className="diff-pill diff-pill--removed">-{removed}</span>}
        </span>
      </button>

      {expanded && (
        <div className="diff-tree-node__body">
          {node.children.map((child) => (
            <DiffTreeNode
              key={child.wbs_path}
              node={child}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
              canRevert={canRevert}
              reverting={reverting}
              onRevertField={onRevertField}
              onRevertActivity={onRevertActivity}
            />
          ))}
          {node.activities.length > 0 && (
            <div className="diff-tree-node__activities" style={{ paddingLeft: `${indent + 28}px` }}>
              <DiffActivityTable
                activities={node.activities}
                canRevert={canRevert}
                reverting={reverting}
                onRevertField={onRevertField}
                onRevertActivity={onRevertActivity}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DiffTreeNode;
